import React from 'react';
import {Box, Stack} from '@mui/material';
import {useTranslation} from 'react-i18next';
import Button from '../base/button/button';
import type {SearchContainerProps} from './types';

/**
 * Layout chrome for standard filter/search controls (does not own fields).
 */
const SearchContainer = ({
    children,
    onSubmit,
    onReset,
    showActions = false,
    submitLabel,
    resetLabel,
    sx,
}: SearchContainerProps): React.ReactElement => {
    const {t} = useTranslation('components', {keyPrefix: 'page-shell.searchContainer'});

    /**
     * Optional native form submit wrapper for non-Form children.
     */
    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        onSubmit?.(e);
    };

    return (
        <Box
            component={onSubmit ? 'form' : 'div'}
            onSubmit={onSubmit ? handleSubmit : undefined}
            sx={[
                {width: '100%'},
                ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
            ]}
        >
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: {xs: 'column', md: 'row'},
                    flexWrap: 'wrap',
                    gap: 2,
                    alignItems: {xs: 'stretch', md: 'flex-end'},
                    width: '100%',
                }}
            >
                {children}
            </Box>
            {showActions && (
                <Stack direction="row" spacing={1} justifyContent="center" sx={{mt: 2}}>
                    {onReset && (
                        <Button type="button" variant="outlined" color="secondary" onClick={onReset}>
                            {resetLabel ?? t((keys) => keys.reset)}
                        </Button>
                    )}
                    {onSubmit && (
                        <Button type="submit" variant="contained" color="primary" sx={{minWidth: 160}}>
                            {submitLabel ?? t((keys) => keys.submit)}
                        </Button>
                    )}
                </Stack>
            )}
        </Box>
    );
};

export default SearchContainer;
